/**
 * The Save & publish draft, kept pure so PortalProvider's rules are testable
 * without React: what a draft is, when it's dirty, what a publish writes, and
 * the gate that lets a publish wait for the app's verdict on the draft's pins.
 */
import { clone, THEME_COLOUR_KEYS, type Brand, type Terms, type Theme } from "@/data/config";
import { toTerminologyInput } from "@/data/brands-api";
import { judges, matchingVerdicts, type PinVerdict, type PinVerdictReport } from "@/data/pin-verdicts";
import { recipeToLegacyHexes, type ThemeRecipe } from "@/data/theme-engine";
import { parseRecipe, recipeReadBack, themeWriteParts } from "@/commands/_content";
import type { PublishThemeInput } from "@/commands/types";

export interface Draft {
  theme: Theme;
  terms: Terms;
  /** The Theme Studio recipe the colours were generated from (a candidate pick
   *  or a knob). Absent on a draft opened from the stored theme: a publish
   *  without one leaves any stored recipe as it is. */
  recipe?: ThemeRecipe | null;
}

/** Resolve the theme to edit for a brand: prefer the requested id, then the
    brand's live theme, then its first theme. A brand always has ≥1 theme. */
export function resolveTheme(brand: Brand, themeId?: string): Theme {
  return (
    (themeId && brand.themes.find((t) => t.id === themeId)) ||
    brand.themes.find((t) => t.id === brand.activeThemeId) ||
    brand.themes[0]!
  );
}

/** The editor draft for a brand's theme, straight from the stored copy. */
export function draftFromBrand(brand: Brand, themeId?: string): Draft {
  return { theme: clone(resolveTheme(brand, themeId)), terms: clone(brand.terms) };
}

/** Whether the draft differs from the stored theme + terms, or carries a
 *  recipe the stored theme doesn't hold (e.g. a font change, same six hexes). */
export function isDraftDirty(draft: Draft, brand: Brand | undefined, themeId: string): boolean {
  const stored = brand?.themes.find((t) => t.id === themeId);
  if (!brand || !stored) return false;
  const recipe = attachedRecipe(draft);
  return (
    JSON.stringify(draft.theme) !== JSON.stringify(stored) ||
    JSON.stringify(draft.terms) !== JSON.stringify(brand.terms) ||
    (!!recipe && !recipeReadBack(stored.treatment, recipe))
  );
}

/**
 * The recipe a publish carries: the draft's, while its six colours are still
 * that recipe's output. A hand edit (Advanced) or a colour held as the app's
 * own detaches it, and the publish goes out without one, as before.
 */
export function attachedRecipe(draft: Draft): ThemeRecipe | null {
  const { recipe, theme } = draft;
  if (!recipe || theme.unsetColors?.length) return null;
  const derived = recipeToLegacyHexes(recipe);
  return THEME_COLOUR_KEYS.every((k) => theme[k] === derived[k]) ? recipe : null;
}

/**
 * Whether a publish of this draft clears the stored recipe: the draft carries
 * none (a hand edit detached it, or it was opened from the stored theme) and
 * its colours are no longer the stored recipe's output. Left in place, the app
 * would keep painting from a recipe that doesn't describe the six colours.
 */
export function clearsStoredRecipe(draft: Draft, stored: Theme | undefined): boolean {
  if (attachedRecipe(draft)) return false;
  const held = parseRecipe(stored?.treatment);
  return !!held && !attachedRecipe({ ...draft, recipe: held });
}

/**
 * What Save & publish sends, as the `publishTheme` command's input: the draft's
 * colours (one the server holds as null kept null), the attached recipe (or a
 * cleared one, see {@link clearsStoredRecipe}), the app's report on these
 * colours (a refused pin is written as null) and the terms. The command
 * composes the write once and says which pins it cleared.
 */
export function composeDraftPublish(
  draft: Draft,
  report: PinVerdictReport | null,
  target: { brandId: string; themeId: string },
  stored?: Theme,
): PublishThemeInput {
  return {
    ...target,
    ...themeWriteParts(draft.theme),
    recipe: attachedRecipe(draft),
    clearRecipe: clearsStoredRecipe(draft, stored),
    pinVerdicts: report,
    terminology: toTerminologyInput(draft.terms),
  };
}

/**
 * The verdicts to show under the picker: the live report's when it judged this
 * draft; otherwise the ones held from the last publish (which cleared a pin and
 * reset the draft), until the draft changes or the app answers again.
 */
export function shownVerdicts(
  live: PinVerdictReport | null,
  held: PinVerdictReport | null,
  colors: Theme,
): PinVerdict[] {
  return judges(live, colors) ? live.verdicts : matchingVerdicts(held, colors);
}

/** How long a publish waits for the app's verdict on the latest draft. */
export const PIN_WAIT_MS = 1500;

/**
 * The provider's view of the preview's verdict exchange, readable from inside
 * an async publish (React state would be stale there).
 */
export function createPinGate() {
  let report: PinVerdictReport | null = null;
  let awaiting = false;
  let waiters: (() => void)[] = [];
  return {
    get report() {
      return report;
    },
    get awaiting() {
      return awaiting;
    },
    setReport(next: PinVerdictReport | null) {
      report = next;
    },
    setAwaiting(next: boolean) {
      awaiting = next;
      if (next) return;
      const done = waiters;
      waiters = [];
      for (const w of done) w();
    },
    /** Resolve once the owed answer arrives, or after `ms`. `judged` is false
     *  when it timed out still waiting (the draft's pins weren't judged). */
    settled(ms = PIN_WAIT_MS): Promise<{ report: PinVerdictReport | null; judged: boolean }> {
      if (!awaiting) return Promise.resolve({ report, judged: true });
      return new Promise((resolve) => {
        const done = () => {
          clearTimeout(timer);
          resolve({ report, judged: true });
        };
        const timer = setTimeout(() => {
          waiters = waiters.filter((w) => w !== done);
          resolve({ report, judged: false });
        }, ms);
        waiters.push(done);
      });
    },
  };
}

export type PinGate = ReturnType<typeof createPinGate>;
